import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { IntentRing } from "@/components/IntentRing";
import { StatusBadge } from "@/components/StatusBadge";
import {
  Plus, Loader2, BookOpen, KeyRound, ArrowRight, Sparkles, Languages,
} from "lucide-react";

export default function Dashboard() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [p, s] = await Promise.all([api.get("/projects"), api.get("/settings")]);
      setProjects(p.data.projects);
      setSettings(s.data);
    } catch (e) {
      toast.error("Could not load your catalog");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const anyProcessing = projects.some((p) =>
    p.editions?.some((e) => ["translating", "verifying", "queued"].includes(e.status)));
  useEffect(() => {
    if (!anyProcessing) return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [anyProcessing]);

  const keysMissing = settings && (!settings.has_ravira || !settings.has_openrouter);

  return (
    <div className="min-h-screen bg-[#f6f5f1]">
      <Header />
      <main className="mx-auto max-w-7xl px-5 py-10 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#d97706] font-mono2">Your studio</div>
            <h1 className="font-display mt-2 text-3xl tracking-tight sm:text-4xl">Catalog</h1>
          </div>
          <Button data-testid="new-project-btn" onClick={() => navigate("/new")}
            className="h-11 rounded-full bg-[#0f172a] px-6 text-white hover:bg-[#1e293b]">
            <Plus className="mr-2 h-4 w-4" /> New manuscript
          </Button>
        </div>

        {keysMissing && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4">
            <div className="flex items-center gap-3 text-sm text-amber-800">
              <KeyRound className="h-5 w-5" />
              Add your RAVIRA and OpenRouter keys for live translation & verification. Until then, runs use a labelled simulation.
            </div>
            <Button data-testid="add-keys-btn" onClick={() => navigate("/settings")} variant="outline"
              className="rounded-full border-amber-300 bg-white text-amber-800 hover:bg-amber-800 hover:text-white">
              Add keys <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </motion.div>
        )}

        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-[#0f172a]" /></div>
        ) : projects.length === 0 ? (
          <EmptyState onNew={() => navigate("/new")} />
        ) : (
          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
            {projects.map((p, i) => (
              <ProjectCard key={p.id} p={p} index={i} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function ProjectCard({ p, index }) {
  const ready = p.editions?.filter((e) => e.epub_ready).length || 0;
  const total = p.editions?.length || 0;
  const avg = total ? Math.round(
    (p.editions.reduce((a, e) => a + (e.fidelity_score || 0), 0)) /
    (p.editions.filter((e) => e.fidelity_score != null).length || 1)) : null;
  const flagged = p.editions?.reduce((a, e) => a + (e.flagged_count || 0), 0) || 0;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: index * 0.06 }}>
      <Link to={`/project/${p.id}`} data-testid={`project-card-${p.id}`}
        className="group block rounded-2xl border border-[#e6e4dd] bg-white p-6 transition-all hover:-translate-y-1 hover:shadow-lg">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h3 className="font-display truncate text-xl tracking-tight">{p.title}</h3>
            <p className="mt-1 text-sm text-[#475569]">by {p.author}</p>
            <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-[#94a3b8]">
              <span className="inline-flex items-center gap-1"><BookOpen className="h-3.5 w-3.5" /> {p.chapter_count} ch</span>
              <span className="inline-flex items-center gap-1"><Languages className="h-3.5 w-3.5" /> {total} languages</span>
            </div>
          </div>
          <IntentRing score={avg} size={64} label={false} />
        </div>

        <div className="mt-5 flex items-center justify-between border-t border-[#e6e4dd] pt-4">
          <div className="flex gap-4 text-sm">
            <div><span className="font-semibold text-[#059669]">{ready}</span><span className="text-[#94a3b8]">/{total} ready</span></div>
            {flagged > 0 && <div className="text-[#e11d48]">{flagged} flagged</div>}
          </div>
          <span className="inline-flex items-center gap-1 text-sm font-medium text-[#0f172a] group-hover:text-[#d97706]">
            Open <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </span>
        </div>
      </Link>
    </motion.div>
  );
}

function EmptyState({ onNew }) {
  return (
    <div className="mt-10 rounded-3xl border border-dashed border-[#d6d3ca] bg-white py-20 text-center">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#0f172a] text-white">
        <Sparkles className="h-6 w-6" />
      </div>
      <h3 className="font-display mt-6 text-2xl tracking-tight">No manuscripts yet</h3>
      <p className="mx-auto mt-2 max-w-sm text-sm text-[#475569]">
        Upload your first book and watch it become a verified, multi-language EPUB catalog.
      </p>
      <Button data-testid="empty-new-btn" onClick={onNew}
        className="mt-6 h-11 rounded-full bg-[#0f172a] px-6 text-white hover:bg-[#1e293b]">
        <Plus className="mr-2 h-4 w-4" /> New manuscript
      </Button>
    </div>
  );
}
